import { useContext, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';
import { IContextMenuItem } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { HOSTS_MAC_ADDRESSES, IHostsMacAddresses } from 'schemas';
import { SettingsContext } from 'settings';

import { useDeleteHost } from '../useDeleteHost';
import { useOnlineBookingEnabled } from '../useOnlineBookingEnabled';
import { useRedeemBooking } from '../useRedeemBooking';
import { useSendShellCommand } from '../useSendShellCommand';
import { useSetServiceMode } from '../useSetServiceMode';
import { useStartClientSession } from '../useStartClientSession';
import { CtxMenuHost, IArgs } from './interfaces';
import {
  getBookingItem,
  getPowerItems,
  getManageHostItem,
  getPenaltyItems,
  getNotifyItem,
  getChangeHostItem,
  getSessionFinishItem,
  getShellItem,
  getShopItem,
  getDepositItem,
  getPostPaymentsItem,
  getGenerateQrItem,
} from './utils';

export const useHostContextMenuItems = (
  handlers: IArgs
): Array<IContextMenuItem<CtxMenuHost>> => {
  const { t } = useContext(LocalizationContext);

  const { cache } = useApolloClient();

  const {
    selectedHostsIds,
    openBookingModal,
    openAllBookingsModal,
    openDepositModal,
    openHostCommentsModal,
    openHostMessageModal,
    openChangeHostModal,
    openManageHostModal,
    openStartSeanceModal,
    openStopClientSessionModal,
    openHostTimeDownModal,
    openRunFileModal,
    openGenerateQrModal,
    unselectHosts,
    hosts,
    openGeneratePinModal,
  } = handlers;

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const {
    state: {
      settings: {
        remoteControlCCBoot,
        remoteControlEnabled,
        remoteControlPassword,
        postPaymentEnabled,
      },
    },
  } = useContext(SettingsContext);

  const isCommentsAvailable = useIsFeatureAvailable('pc_comments');
  const isRunFileAvailable = useIsFeatureAvailable('remote_exec');
  const isBookingAvailable = useIsFeatureAvailable('booking');
  const isPenaltiesAvailable = useIsFeatureAvailable('host_penalty');
  const isHostNotifyAvailable = useIsFeatureAvailable('host_notification');
  const isPostPaymentsAvailable = useIsFeatureAvailable('post_payments');
  const isRemoteControlAvailable =
    useIsFeatureAvailable('remote_control') && remoteControlEnabled;

  const disabledPowerOn = useMemo(() => {
    if (!selectedHostsIds || selectedHostsIds.length < 2) return false;

    const hostsData = selectedHostsIds
      .map(
        (hostId) =>
          cache.readQuery<IHostsMacAddresses>({
            query: HOSTS_MAC_ADDRESSES,
            variables: { id: hostId },
          })?.hostOverview.mac_addr ?? ''
      )
      .filter((val) => val);

    return selectedHostsIds.length !== hostsData.length;
  }, [cache, selectedHostsIds]);

  const redeemBooking = useRedeemBooking();

  const setServiceMode = useSetServiceMode(t);

  const { sendCommand, sendCommands } = useSendShellCommand();

  const updateOnlineBookingEnabled = useOnlineBookingEnabled(t);

  const deleteHost = useDeleteHost(t);

  const startClientSession = useStartClientSession(t);

  const commonItems: Array<IContextMenuItem<CtxMenuHost>> = [];

  const shopItem = getShopItem({
    t,
    selectedHostsIds,
    permissions,
    openStartSeanceModal,
    hosts,
  });

  const depositItem = getDepositItem({
    t,
    selectedHostsIds,
    openDepositModal,
    permissions,
  });

  const bookingItems = getBookingItem({
    t,
    redeemBooking,
    openBookingModal,
    openAllBookingsModal,
    isBookingAvailable,
    selectedHostsIds,
    permissions,
    hosts,
  });

  const postPaymentsItem = getPostPaymentsItem({
    t,
    isPostPaymentsAvailable,
    permissions,
    startClientSession,
    selectedHostsIds,
  });

  const penaltyItems = getPenaltyItems({
    t,
    isPenaltiesAvailable,
    openHostTimeDownModal,
    permissions,
    selectedHostsIds,
  });

  const notifyItem = getNotifyItem({
    t,
    isHostNotifyAvailable,
    openHostMessageModal,
    permissions,
  });

  const powerItems = getPowerItems({
    t,
    selectedHostsIds,
    sendCommand,
    permissions,
    disabledPowerOn,
    hosts,
    sendCommands,
  });

  const changeHostItem = getChangeHostItem({
    t,
    openChangeHostModal,
    selectedHostsIds,
    permissions,
  });

  const sessionFinishItem = getSessionFinishItem({
    t,
    permissions,
    selectedHostsIds,
    openStopClientSessionModal,
  });

  const manageHostItems = getManageHostItem({
    t,
    permissions,
    deleteHost,
    openManageHostModal,
    openHostCommentsModal,
    unselectHosts,
    selectedHostsIds,
    isRunFileAvailable,
    isRemoteControlAvailable,
    remoteControlCCBoot,
    remoteControlPassword,
    isCommentsAvailable,
    openRunFileModal,
    setServiceMode,
    updateOnlineBookingEnabled,
    hosts,
    openGeneratePinModal,
  });

  const shellItem = getShellItem({
    t,
    permissions,
    selectedHostsIds,
    sendCommand,
    sendCommands,
  });

  const generateQrItem = getGenerateQrItem({
    t,
    openGenerateQrModal,
    permissions,
    selectedHostsIds,
  });

  if (shopItem) commonItems.push(shopItem);

  if (postPaymentsItem && postPaymentEnabled)
    commonItems.push(postPaymentsItem);

  if (depositItem) commonItems.push(depositItem);

  if (bookingItems) commonItems.push(bookingItems);

  if (penaltyItems) commonItems.push(penaltyItems);

  if (changeHostItem) commonItems.push(changeHostItem);

  if (generateQrItem) commonItems.push(generateQrItem);

  if (sessionFinishItem) commonItems.push(sessionFinishItem);

  if (notifyItem) commonItems.push(notifyItem);

  if (powerItems) commonItems.push(powerItems);

  if (manageHostItems) commonItems.push(manageHostItems);

  if (shellItem) commonItems.push(shellItem);

  return commonItems;
};
